import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import backpack from '~/assets/icons/backpack_2.svg';
import box from '~/assets/icons/box_2.svg';
import home from '~/assets/icons/home.svg';
import infoRound from '~/assets/icons/info_round_2.svg';
import pack from '~/assets/icons/pack.svg';
import trash from '~/assets/icons/trash.svg';
import trunk from '~/assets/icons/trunk.svg';
import user from '~/assets/icons/user.svg';
import { playSound, Sound, stopSound } from '~/assets/sounds';
import { ScrollContainer } from '~/components/ui/ScrollContainer';
import { rage, useInputAction, useObservable } from '~/rage';
import {
	ClothesType,
	InputAction,
	InventoryCell as InventoryCellItem,
	InventoryType as InventoryCellType,
	InventoryType,
	Item,
	ItemAction,
	ItemType,
	KeyPressStates,
	PackedInventory,
} from '~/shared';
import {
	useInventoryActions,
	useInventoryCells,
	useInventoryDrag,
} from '../../hooks';
import {
	setDelZoneActive,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import {
	clothesPlaceholders,
	getCharacterCellByItemType,
} from '../Inventory/Inventory.model';
import { InventoryCell } from '../InventoryCell';
import { ShopBanner } from '../ShopBanner';
import { Switch } from '../Switch';
import { TextLabel } from '../TextLabel';
import {
	GRID_STYLES,
	StyledBannerContainer,
	StyledContainer,
	StyledCountContainer,
	StyledDelZoneBgStripes,
	StyledDelZoneContainer,
	StyledFlexContainer,
	StyledGridContainer,
	StyledInventoryContainer,
	StyledQuickAccessHeading,
	StyledSectionHeading,
} from './InventoryMain.styled';

const InventoryMain = () => {
	const dispatch = useLayoutDispatch();
	const {
		inventoryIds,
		secondaryInventoryId,
		playerInventory,
		backpackInventory,
		secondaryInventory,
		inventoryCells,
		characterCells,
		hotbarCells,
		backpackCells,
		secondaryCells,
		hasBackpack,
		secondaryInventoryType,
	} = useInventoryCells();
	const delZoneActive = useLayoutSelector(
		(state) => state.inventory.delZoneActive,
	);
	const secondaryInventoryTitle = useObservable(
		rage.inventory.getSecondaryInventoryTitle(),
	);
	const activeItems = useObservable(rage.inventory.getActiveItems());

	const getInventoryHeaderByType = (
		type: InventoryType | null,
	): { text: string; icon: string | null } => {
		if (type === InventoryType.VEHICLE) {
			return { text: 'Багажник', icon: trunk };
		} else if (type === InventoryType.HOUSE) {
			return { text: 'Дом', icon: home };
		} else if (type === InventoryType.DROPPED) {
			return { text: 'Выброшенное', icon: null };
		}

		return { text: '???', icon: null };
	};

	const getCellsByType = (
		type: InventoryCellType,
	): InventoryCellItem[] | null => {
		switch (type) {
			case InventoryCellType.EQUIPMENT:
				return characterCells;
			case InventoryCellType.PLAYER:
				return inventoryCells;
			case InventoryCellType.BACKPACK:
				return backpackCells;
			case InventoryCellType.VEHICLE:
			case InventoryCellType.HOUSE:
			case InventoryCellType.DROPPED:
				return secondaryCells;
			case InventoryCellType.HOTBAR:
				return hotbarCells;
			default:
				return null;
		}
	};

	const { handleInventoryAction, handleCharacterAction, getAvailableActions } =
		useInventoryActions();

	const { dragging, draggedItem, handleDragStart, handleDragEnd } =
		useInventoryDrag();

	const [isFastMove, setIsFastMove] = useState(false);

	const showBanner = true;

	const handleFastMoveKey = useCallback((state: KeyPressStates) => {
		setIsFastMove(state === KeyPressStates.Pressed);
	}, []);

	useInputAction(InputAction.InventoryFastMove, handleFastMoveKey, {
		state: KeyPressStates.Both,
	});

	useEffect(() => {
		playSound(Sound.OpenInventory);
		return () => {
			stopSound(Sound.OpenInventory);
		};
	}, []);

	const [hoveredClothesType, setHoveredClothesType] =
		useState<ClothesType | null>(null);
	const [hoveredIsBackpack, setHoveredIsBackpack] = useState(false);
	const [dragOverTarget, setDragOverTarget] = useState<{
		type: InventoryCellType;
		index: number;
	} | null>(null);

	const handleDragOver = (
		e: React.DragEvent,
		targetType: InventoryCellType,
		targetIndex: number,
	) => {
		if (targetType === InventoryCellType.DROPPED) return;
		e.preventDefault();
		if (dragging && draggedItem) {
			setDragOverTarget({ type: targetType, index: targetIndex });
		}
	};

	const handleDrop = (
		e: React.DragEvent,
		targetCell: InventoryCellItem | null,
		targetType: InventoryCellType,
		targetIndex: number,
	) => {
		e.preventDefault();
		setDragOverTarget(null);
		if (targetCell?.isClosed) return;
		if (!draggedItem?.cell.item) return;

		const sourceType = draggedItem.sourceType;

		if (targetType === InventoryCellType.DROPPED) {
			handleDragEnd();
			return;
		}
		const sourceCell = draggedItem.cell;
		const sourceItem = sourceCell.item;

		// if (!canDropItem(targetCell, sourceItem, targetType)) {
		// 	return;
		// }

		const sourceCells = getCellsByType(sourceType);
		const targetCells = getCellsByType(targetType);

		if (!sourceCells || !targetCells) return;

		const sourceIndex = draggedItem.cell.position - 1;

		if (sourceIndex === -1 || targetIndex === -1) return;

		if (sourceType === targetType && sourceIndex === targetIndex) {
			handleDragEnd();
			return;
		}

		const typeToId: Record<InventoryCellType, string | undefined> = {
			[InventoryCellType.PLAYER]: inventoryIds[InventoryType.PLAYER],
			[InventoryCellType.EQUIPMENT]: inventoryIds[InventoryType.EQUIPMENT],
			[InventoryCellType.HOTBAR]: inventoryIds[InventoryType.HOTBAR],
			[InventoryCellType.BACKPACK]: inventoryIds[InventoryType.BACKPACK],
			[InventoryCellType.VEHICLE]: secondaryInventoryId ?? undefined,
			[InventoryCellType.HOUSE]: secondaryInventoryId ?? undefined,
			[InventoryCellType.EXCHANGE]: undefined,
			[InventoryCellType.DROPPED]: secondaryInventoryId ?? undefined,
		};

		if (sourceType === targetType) {
			const invId = typeToId[sourceType];
			if (invId) {
				rage.inventory
					.moveItem(
						invId,
						sourceItem!.id,
						sourceItem!.count,
						sourceItem!.data,
						sourceIndex,
						targetIndex,
					)
					.catch(() => undefined);
				return;
			}
		}

		// Use typeToId which includes DROPPED -> secondaryInventoryId mapping
		const fromId = typeToId[sourceType];
		const toId = typeToId[targetType];
		if (fromId && toId && sourceItem) {
			rage.inventory
				.transferItem(
					sourceItem.id,
					sourceItem.count,
					sourceItem.data,
					fromId,
					sourceIndex,
					toId,
					targetIndex,
				)
				.catch(() => undefined);
		}

		handleDragEnd();
		setDragOverTarget(null);
	};

	const handleTrashDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setDragOverTarget(null);
		if (draggedItem && delZoneActive) {
			const sourceType = draggedItem.sourceType;
			const sourceCell = draggedItem.cell;
			const item = sourceCell.item;
			if (!item) return;

			const typeToId: Record<InventoryCellType, string | undefined> = {
				[InventoryCellType.PLAYER]: inventoryIds[InventoryType.PLAYER],
				[InventoryCellType.EQUIPMENT]: inventoryIds[InventoryType.EQUIPMENT],
				[InventoryCellType.HOTBAR]: inventoryIds[InventoryType.HOTBAR],
				[InventoryCellType.BACKPACK]: inventoryIds[InventoryType.BACKPACK],
				[InventoryCellType.VEHICLE]: secondaryInventoryId ?? undefined,
				[InventoryCellType.HOUSE]: secondaryInventoryId ?? undefined,
				[InventoryCellType.EXCHANGE]: undefined,
				[InventoryCellType.DROPPED]: undefined,
			};

			const invId = typeToId[sourceType];
			if (!invId) return;

			rage.inventory
				.performItemAction(
					invId,
					item.id,
					item.count,
					item.data,
					sourceCell.position - 1,
					ItemAction.DROP,
				)
				.catch(() => undefined);

			handleDragEnd();
			setDragOverTarget(null);
		}
	};

	const handleDragEndWithCleanup = () => {
		handleDragEnd();
		setDragOverTarget(null);
	};

	const hasFreeSlot = (inv: PackedInventory | null): boolean => {
		if (!inv) return false;
		const filled = inv.items.filter((stack) => stack != null).length;
		if (filled < inv.capacity) return true;
		return inv.items.some((stack) => stack == null);
	};

	const handleFastMove = (
		e: React.MouseEvent,
		item: Item,
		sourceType: InventoryCellType,
		index: number,
	): boolean => {
		if (!isFastMove) return false;

		let externalType: InventoryCellType | null = null;
		if (secondaryInventoryType === InventoryType.VEHICLE) {
			externalType = InventoryCellType.VEHICLE;
		} else if (secondaryInventoryType === InventoryType.HOUSE) {
			externalType = InventoryCellType.HOUSE;
		} else if (secondaryInventoryType === InventoryType.DROPPED) {
			externalType = InventoryCellType.DROPPED;
		}

		if (!externalType) return false;

		const isFromPrimary =
			sourceType === InventoryCellType.PLAYER ||
			sourceType === InventoryCellType.BACKPACK;
		const isFromExternal = sourceType === externalType;

		if (!isFromPrimary && !isFromExternal) return false;

		let targetType: InventoryCellType;
		if (isFromPrimary) {
			targetType = externalType;
		} else if (hasFreeSlot(playerInventory)) {
			targetType = InventoryCellType.PLAYER;
		} else if (hasBackpack && hasFreeSlot(backpackInventory)) {
			targetType = InventoryCellType.BACKPACK;
		} else {
			return false;
		}

		const typeToId: Record<InventoryCellType, string | undefined> = {
			[InventoryCellType.PLAYER]: inventoryIds[InventoryType.PLAYER],
			[InventoryCellType.EQUIPMENT]: inventoryIds[InventoryType.EQUIPMENT],
			[InventoryCellType.HOTBAR]: inventoryIds[InventoryType.HOTBAR],
			[InventoryCellType.BACKPACK]: inventoryIds[InventoryType.BACKPACK],
			[InventoryCellType.VEHICLE]: secondaryInventoryId ?? undefined,
			[InventoryCellType.HOUSE]: secondaryInventoryId ?? undefined,
			[InventoryCellType.EXCHANGE]: undefined,
			[InventoryCellType.DROPPED]: secondaryInventoryId ?? undefined,
		};

		const fromId = typeToId[sourceType];
		const toId = typeToId[targetType];
		if (!fromId || !toId) return false;

		rage.inventory
			.transferItem(
				item.id,
				item.count,
				item.data,
				fromId,
				index,
				toId,
				undefined,
			)
			.catch(() => undefined);

		e.preventDefault();
		return true;
	};

	const handleFastEquip = (
		e: React.MouseEvent,
		item: Item | undefined,
		sourceType: InventoryCellType,
		index: number,
	): boolean => {
		if (!isFastMove) return false;

		if (!item) return false;

		const isClothes = item.type === ItemType.CLOTHES;
		const isBackpack = item.type === ItemType.BACKPACK;
		const hasClothesType = item.data?.clothes?.type !== undefined;

		if (!isClothes && !isBackpack && !hasClothesType) {
			return false;
		}

		if (sourceType === InventoryCellType.EQUIPMENT) {
			const availableActions = getAvailableActions(item, sourceType);
			if (!availableActions.includes(ItemAction.UNEQUIP)) {
				return false;
			}

			if (
				!hasFreeSlot(playerInventory) &&
				!(hasBackpack && hasFreeSlot(backpackInventory))
			) {
				return false;
			}

			handleCharacterAction(ItemAction.UNEQUIP, item, index);
			e.preventDefault();
			return true;
		}

		const targetCellId = getCharacterCellByItemType(item.data);
		if (!targetCellId) return false;

		const targetCell = characterCells.find((cell) => cell.id === targetCellId);
		if (!targetCell || targetCell.item) {
			return false;
		}

		const availableActions = getAvailableActions(item, sourceType);
		if (!availableActions.includes(ItemAction.EQUIP)) {
			return false;
		}

		handleInventoryAction(ItemAction.EQUIP, item, sourceType, index);
		e.preventDefault();
		return true;
	};

	const { text: secHeaderText, icon: secHeaderIcon } = useMemo(
		() => getInventoryHeaderByType(secondaryInventoryType),
		[secondaryInventoryType],
	);

	const showDelZone = useMemo(
		() =>
			secondaryInventoryType !== InventoryType.DROPPED || !secondaryInventoryId,
		[secondaryInventoryType, secondaryInventoryId],
	);

	useInputAction(InputAction.InventoryPageToggle, () => {
		rage.game.closeMainMenu();
	});

	return (
		<StyledContainer $banner={showBanner}>
			<StyledBannerContainer>
				{showBanner && <ShopBanner />}
			</StyledBannerContainer>
			<StyledInventoryContainer>
				<StyledFlexContainer $gap={45}>
					<StyledFlexContainer $gap={20}>
						<StyledSectionHeading>
							<img src={user} />
							<TextLabel>Мой персонаж</TextLabel>
						</StyledSectionHeading>
						<StyledGridContainer
							$columns={GRID_STYLES.columns.character}
							$rowgap={10}
							$colgap={55}
						>
							{characterCells.map((cell, index) => {
								if (cell.height === 0) {
									return null;
								}

								// if (!cell.height) {
								// 	return <div key={`${cell.id}-${index}`} />
								// }

								return (
									<InventoryCell
										key={`${cell.id}-${index}`}
										item={
											cell.item
												? {
														...cell.item,
														availableActions: getAvailableActions(
															cell.item,
															InventoryCellType.EQUIPMENT,
														),
												  }
												: undefined
										}
										height={clothesPlaceholders[index]?.height ?? 1}
										isDragging={dragging}
										hint={
											dragOverTarget?.type === InventoryCellType.EQUIPMENT &&
											dragOverTarget?.index === index
										}
										onDragStart={(e) =>
											handleDragStart(e, cell, InventoryCellType.EQUIPMENT)
										}
										onDragOver={(e) =>
											handleDragOver(e, InventoryCellType.EQUIPMENT, index)
										}
										onDrop={(e) =>
											handleDrop(e, cell, InventoryCellType.EQUIPMENT, index)
										}
										onDragEnd={handleDragEndWithCleanup}
										placeholderImage={
											clothesPlaceholders[index]?.placeholderImage
										}
										placeholderText={
											clothesPlaceholders[index]?.placeholderText
										}
										onAction={(action, item) =>
											handleCharacterAction(action, item, cell.position - 1)
										}
										onClick={(e) =>
											handleFastEquip(
												e,
												cell.item,
												InventoryCellType.EQUIPMENT,
												cell.position - 1,
											)
										}
										help={Boolean(
											(!cell.item &&
												((hoveredClothesType &&
													getCharacterCellByItemType({
														clothes: {
															type: hoveredClothesType,
														},
													} as any) === cell.id) ||
													(hoveredIsBackpack &&
														cell.allowedTypes?.includes(ItemType.BACKPACK)))) ||
												(cell.item &&
													((cell.item.type === ItemType.CLOTHES &&
														hoveredClothesType ===
															cell.item.data?.clothes?.type) ||
														(cell.item.data?.clothes?.type ===
															ClothesType.BACKPACK &&
															hoveredIsBackpack))),
										)}
										helpHover={Boolean(
											dragging &&
												((draggedItem?.cell.item?.type === ItemType.CLOTHES &&
													getCharacterCellByItemType(
														draggedItem.cell.item.data as any,
													) === cell.id) ||
													(draggedItem?.cell.item?.data?.clothes?.type ===
														ClothesType.BACKPACK &&
														cell.allowedTypes?.includes(ItemType.BACKPACK))),
										)}
										onMouseEnter={() => {
											if (!cell.item) return;

											if (cell.item.type === ItemType.CLOTHES) {
												setHoveredClothesType(
													cell.item.data?.clothes?.type ?? null,
												);
												setHoveredIsBackpack(false);
											} else if (
												cell.item.data?.clothes?.type === ClothesType.BACKPACK
											) {
												setHoveredIsBackpack(true);
												setHoveredClothesType(null);
											}
										}}
										onMouseLeave={() => {
											setHoveredClothesType(null);
											setHoveredIsBackpack(false);
										}}
									/>
								);
							})}
						</StyledGridContainer>
					</StyledFlexContainer>
					<StyledFlexContainer $gap={10} $align={'center'}>
						<StyledQuickAccessHeading>
							<div className={'line'} />
							<div className={'center'}>
								<img src={infoRound} />
								<TextLabel>Ячейки с быстрым доступом</TextLabel>
							</div>
							<div className={'line'} />
						</StyledQuickAccessHeading>
						<StyledGridContainer $columns={GRID_STYLES.columns.quickAccess}>
							{hotbarCells.map((cell, index) => (
								<InventoryCell
									key={`${cell.id}-${index}`}
									item={
										cell.item
											? {
													...cell.item,
													availableActions: getAvailableActions(
														cell.item,
														InventoryCellType.HOTBAR,
														activeItems.some(
															(item) =>
																item.inventoryType === InventoryType.HOTBAR &&
																item.itemSlot === cell.position - 1,
														),
													),
											  }
											: undefined
									}
									quickAccessButton={cell.position.toString()}
									isDragging={dragging}
									activeHand={activeItems.some(
										(item) =>
											item.inventoryType === InventoryType.HOTBAR &&
											item.itemSlot === cell.position - 1,
									)}
									hint={
										dragOverTarget?.type === InventoryCellType.HOTBAR &&
										dragOverTarget?.index === index
									}
									onDragStart={(e) =>
										handleDragStart(e, cell, InventoryCellType.HOTBAR)
									}
									onDragOver={(e) =>
										handleDragOver(e, InventoryCellType.HOTBAR, index)
									}
									onDrop={(e) =>
										handleDrop(e, cell, InventoryCellType.HOTBAR, index)
									}
									onDragEnd={handleDragEndWithCleanup}
									onAction={(action, item) =>
										handleInventoryAction(
											action,
											item,
											InventoryCellType.HOTBAR,
											cell.position - 1,
										)
									}
									onClick={(e) =>
										handleFastEquip(
											e,
											cell.item,
											InventoryCellType.HOTBAR,
											cell.position - 1,
										)
									}
									onMouseEnter={() => {
										if (!cell?.item) return;

										if (cell?.item.type === ItemType.CLOTHES) {
											setHoveredClothesType(
												cell?.item.data?.clothes?.type ?? null,
											);
											setHoveredIsBackpack(false);
										} else if (
											cell?.item.data?.clothes?.type === ClothesType.BACKPACK
										) {
											setHoveredIsBackpack(true);
											setHoveredClothesType(null);
										}
									}}
									onMouseLeave={() => {
										setHoveredClothesType(null);
										setHoveredIsBackpack(false);
									}}
								/>
							))}
						</StyledGridContainer>
					</StyledFlexContainer>
				</StyledFlexContainer>
				<StyledFlexContainer $gap={40}>
					<StyledFlexContainer $gap={20}>
						<StyledFlexContainer
							$direction={'row'}
							$align={'center'}
							$justify={'space-between'}
						>
							<StyledSectionHeading>
								<img src={box} />
								<TextLabel>Мой инвентарь</TextLabel>
							</StyledSectionHeading>
							<StyledCountContainer>
								<TextLabel>
									<span>
										{inventoryCells.filter((cell) => cell?.item).length}
									</span>{' '}
									/ {playerInventory?.capacity ?? 0}
								</TextLabel>
								<img src={pack} />
							</StyledCountContainer>
						</StyledFlexContainer>
						<StyledGridContainer $columns={GRID_STYLES.columns.inventory}>
							{inventoryCells.map((cell, index) => (
								<InventoryCell
									key={`${cell?.id || `empty-${index}`}-${index}`}
									item={
										cell?.item
											? {
													...cell.item,
													availableActions: getAvailableActions(
														cell?.item,
														InventoryCellType.PLAYER,
														activeItems.some(
															(item) =>
																item.inventoryType === InventoryType.PLAYER &&
																item.itemSlot === cell.position - 1,
														),
													),
											  }
											: undefined
									}
									isDragging={dragging}
									activeHand={activeItems.some(
										(item) =>
											item.inventoryType === InventoryType.PLAYER &&
											item.itemSlot === cell.position - 1,
									)}
									hint={
										dragOverTarget?.type === InventoryCellType.PLAYER &&
										dragOverTarget?.index === index
									}
									onDragStart={(e) => {
										if (cell) {
											handleDragStart(e, cell, InventoryCellType.PLAYER);
										}
									}}
									onDragOver={(e) =>
										handleDragOver(e, InventoryCellType.PLAYER, index)
									}
									onDrop={(e) =>
										handleDrop(e, cell, InventoryCellType.PLAYER, index)
									}
									onDragEnd={handleDragEndWithCleanup}
									onAction={(action, item) =>
										handleInventoryAction(
											action,
											item,
											InventoryCellType.PLAYER,
											cell.position - 1,
										)
									}
									onClick={(e) =>
										handleFastEquip(
											e,
											cell?.item,
											InventoryCellType.PLAYER,
											cell.position - 1,
										)
									}
									onContextMenu={(e) =>
										cell?.item
											? handleFastMove(
													e,
													cell.item,
													InventoryCellType.PLAYER,
													index,
											  )
											: false
									}
									onMouseEnter={() => {
										if (!cell?.item) return;

										if (cell?.item.type === ItemType.CLOTHES) {
											setHoveredClothesType(
												cell?.item.data?.clothes?.type ?? null,
											);
											setHoveredIsBackpack(false);
										} else if (
											cell?.item.data?.clothes?.type === ClothesType.BACKPACK
										) {
											setHoveredIsBackpack(true);
											setHoveredClothesType(null);
										}
									}}
									onMouseLeave={() => {
										setHoveredClothesType(null);
										setHoveredIsBackpack(false);
									}}
								/>
							))}
						</StyledGridContainer>
					</StyledFlexContainer>
					<StyledFlexContainer $gap={20}>
						<StyledFlexContainer
							$direction={'row'}
							$align={'center'}
							$justify={'space-between'}
						>
							<StyledSectionHeading>
								<img src={backpack} />
								<TextLabel>Мой рюкзак</TextLabel>
							</StyledSectionHeading>
							{hasBackpack ? (
								<StyledCountContainer>
									<TextLabel>
										<span>
											{backpackCells.filter((cell) => cell.item).length}
										</span>{' '}
										/ {backpackCells.filter((cell) => !cell.isClosed).length}
									</TextLabel>
									<img src={pack} />
								</StyledCountContainer>
							) : (
								<TextLabel
									style={{ fontSize: 16, color: '#ffffff', opacity: 0.25 }}
								>
									Рюкзак отсутствует
								</TextLabel>
							)}
						</StyledFlexContainer>
						<ScrollContainer height={170}>
							<StyledGridContainer $columns={GRID_STYLES.columns.backpack}>
								{backpackCells.map((cell, index) => (
									<InventoryCell
										key={`${cell.id}-${index}`}
										item={
											hasBackpack && cell.item
												? {
														...cell.item,
														availableActions: getAvailableActions(
															cell.item,
															InventoryCellType.BACKPACK,
															activeItems.some(
																(item) =>
																	item.inventoryType ===
																		InventoryType.BACKPACK &&
																	item.itemSlot === cell.position - 1,
															),
														),
												  }
												: undefined
										}
										closed={!hasBackpack || cell.isClosed}
										isDragging={dragging}
										activeHand={activeItems.some(
											(item) =>
												item.inventoryType === InventoryType.BACKPACK &&
												item.itemSlot === cell.position - 1,
										)}
										hint={
											dragOverTarget?.type === InventoryCellType.BACKPACK &&
											dragOverTarget?.index === index
										}
										onDragStart={(e) =>
											handleDragStart(e, cell, InventoryCellType.BACKPACK)
										}
										onDragOver={(e) =>
											handleDragOver(e, InventoryCellType.BACKPACK, index)
										}
										onDrop={(e) =>
											handleDrop(e, cell, InventoryCellType.BACKPACK, index)
										}
										onDragEnd={handleDragEndWithCleanup}
										onAction={(action, item) =>
											handleInventoryAction(
												action,
												item,
												InventoryCellType.BACKPACK,
												cell.position - 1,
											)
										}
										onClick={(e) =>
											handleFastEquip(
												e,
												cell.item,
												InventoryCellType.BACKPACK,
												cell.position - 1,
											)
										}
										onContextMenu={(e) =>
											cell.item
												? handleFastMove(
														e,
														cell.item,
														InventoryCellType.BACKPACK,
														index,
												  )
												: false
										}
										onMouseEnter={() => {
											if (!cell.item) return;

											if (cell.item.type === ItemType.CLOTHES) {
												setHoveredClothesType(
													cell.item.data?.clothes?.type ?? null,
												);
												setHoveredIsBackpack(false);
											} else if (
												cell.item.data?.clothes?.type === ClothesType.BACKPACK
											) {
												setHoveredIsBackpack(true);
												setHoveredClothesType(null);
											}
										}}
										onMouseLeave={() => {
											setHoveredClothesType(null);
											setHoveredIsBackpack(false);
										}}
									/>
								))}
							</StyledGridContainer>
						</ScrollContainer>
					</StyledFlexContainer>
				</StyledFlexContainer>
				<StyledFlexContainer $gap={40}>
					{secondaryInventoryId && (
						<StyledFlexContainer $gap={20}>
							<StyledFlexContainer
								$direction={'row'}
								$align={'center'}
								$justify={'space-between'}
							>
								<StyledSectionHeading>
									{secHeaderIcon && <img src={secHeaderIcon} />}
									<TextLabel
										style={{ display: 'flex', alignItems: 'center', gap: 5 }}
									>
										{secHeaderText}
										{secondaryInventoryTitle && (
											<span
												style={{
													fontWeight: 300,
													fontStyle: 'italic',
													opacity: 0.5,
													display: 'inline-block',
													whiteSpace: 'nowrap',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													maxWidth: '150px',
												}}
											>
												({secondaryInventoryTitle})
											</span>
										)}
									</TextLabel>
								</StyledSectionHeading>
								<StyledCountContainer>
									<TextLabel>
										<span>
											{secondaryCells.filter((cell) => cell.item).length}
										</span>{' '}
										/ {secondaryInventory?.capacity ?? 0}
									</TextLabel>
									<img src={pack} />
								</StyledCountContainer>
							</StyledFlexContainer>
							<ScrollContainer height={showDelZone ? 350 : 600}>
								<StyledGridContainer $columns={GRID_STYLES.columns.trunk}>
									{secondaryCells.map((cell, index) => (
										<InventoryCell
											key={`${cell.id}-${index}`}
											item={
												cell.item
													? {
															...cell.item,
															availableActions: getAvailableActions(
																cell.item,
																secondaryInventoryType as
																	| InventoryType.VEHICLE
																	| InventoryType.HOUSE
																	| InventoryType.DROPPED,
																activeItems.some(
																	(item) =>
																		item.inventoryType ===
																			secondaryInventoryType &&
																		item.itemSlot === cell.position - 1,
																),
															),
													  }
													: undefined
											}
											isDragging={dragging}
											activeHand={activeItems.some(
												(item) =>
													item.inventoryType === secondaryInventoryType &&
													item.itemSlot === cell.position - 1,
											)}
											hint={
												dragOverTarget?.type === secondaryInventoryType &&
												dragOverTarget?.index === index
											}
											onDragStart={(e) =>
												handleDragStart(
													e,
													cell,
													secondaryInventoryType as
														| InventoryType.VEHICLE
														| InventoryType.HOUSE
														| InventoryType.DROPPED,
												)
											}
											onDragOver={(e) =>
												handleDragOver(
													e,
													secondaryInventoryType as
														| InventoryType.VEHICLE
														| InventoryType.HOUSE
														| InventoryType.DROPPED,
													index,
												)
											}
											onDrop={(e) =>
												handleDrop(
													e,
													cell,
													secondaryInventoryType as
														| InventoryType.VEHICLE
														| InventoryType.HOUSE
														| InventoryType.DROPPED,
													index,
												)
											}
											onDragEnd={handleDragEndWithCleanup}
											onAction={(action, item) =>
												handleInventoryAction(
													action,
													item,
													secondaryInventoryType as
														| InventoryType.VEHICLE
														| InventoryType.HOUSE
														| InventoryType.DROPPED,
													cell.position - 1,
												)
											}
											onClick={(e) =>
												handleFastEquip(
													e,
													cell.item,
													secondaryInventoryType as
														| InventoryType.VEHICLE
														| InventoryType.HOUSE
														| InventoryType.DROPPED,
													cell.position - 1,
												)
											}
											onContextMenu={(e) =>
												cell.item
													? handleFastMove(
															e,
															cell.item,
															secondaryInventoryType as
																| InventoryType.VEHICLE
																| InventoryType.HOUSE
																| InventoryType.DROPPED,
															index,
													  )
													: false
											}
											onMouseEnter={() => {
												if (!cell.item) return;

												if (cell.item.type === ItemType.CLOTHES) {
													setHoveredClothesType(
														cell.item.data?.clothes?.type ?? null,
													);
													setHoveredIsBackpack(false);
												} else if (
													cell.item.data?.clothes?.type === ClothesType.BACKPACK
												) {
													setHoveredIsBackpack(true);
													setHoveredClothesType(null);
												}
											}}
											onMouseLeave={() => {
												setHoveredClothesType(null);
												setHoveredIsBackpack(false);
											}}
										/>
									))}
								</StyledGridContainer>
							</ScrollContainer>
						</StyledFlexContainer>
					)}
					{showDelZone && (
						<StyledFlexContainer $gap={20}>
							<StyledFlexContainer
								$direction={'row'}
								$justify={'space-between'}
							>
								<StyledSectionHeading>
									<img src={trash} />
									<TextLabel>Зона утилизации</TextLabel>
								</StyledSectionHeading>
								<StyledFlexContainer $direction={'row'} $gap={10}>
									<TextLabel
										style={{ fontSize: 16, color: '#ffffff', opacity: 0.25 }}
									>
										Вкл / Выкл
									</TextLabel>
									<Switch
										active={delZoneActive}
										onClick={() => dispatch(setDelZoneActive(!delZoneActive))}
									/>
								</StyledFlexContainer>
							</StyledFlexContainer>
							<StyledDelZoneContainer
								$active={delZoneActive}
								$full={!secondaryInventoryType || !secondaryInventoryId}
								$hover={
									draggedItem !== null && draggedItem.cell.item !== undefined
								}
								onDragOver={(e) => {
									e.preventDefault();
									if (dragging && draggedItem) {
										setDragOverTarget(null);
									}
								}}
								onDrop={handleTrashDrop}
							>
								<StyledDelZoneBgStripes />
								{draggedItem !== null &&
								draggedItem.cell.item !== undefined &&
								delZoneActive ? (
									<TextLabel
										style={{
											fontSize: 16,
											fontWeight: 500,
											color: '#C71E1E',
											textShadow: '0px 0px 10px rgba(199, 30, 30, 0.5)',
											textAlign: 'center',
										}}
									>
										Утилизировать
										<br />
										предмет
									</TextLabel>
								) : (
									<Fragment>
										<TextLabel
											style={{
												fontSize: 16,
												fontWeight: 500,
												color: '#ffffff',
											}}
										>
											Зона утилизации
										</TextLabel>
										<TextLabel
											style={{
												fontSize: 12,
												fontStyle: 'italic',
												color: 'rgba(255,255,255,0.5)',
											}}
										>
											Перетащите в эту зону вещи
											<br />
											которые вы хотите выкинуть
										</TextLabel>
									</Fragment>
								)}
							</StyledDelZoneContainer>
						</StyledFlexContainer>
					)}
				</StyledFlexContainer>
			</StyledInventoryContainer>
		</StyledContainer>
	);
};

export { InventoryMain };
